import { useCallback, useEffect, useState } from 'react';
import { noteTyping } from '../sync/activity';

/**
 * How long a field holds its own text before deferring to the stored copy.
 * Restarted by every keystroke, so this only ever expires when a write really
 * did not come back — a sync pull that overwrote it, or a failed write.
 */
const SETTLE_MS = 3000;

/**
 * Reconciles text the user is typing with the copy of it coming back from the
 * database.
 *
 * Every keystroke is written to Dexie and handed back a moment later by the
 * live query — by which time a fast typist has already typed more, so the value
 * arriving is behind what is on screen. Rendering it verbatim rewinds the field
 * mid-word, and because the rewound text is then written back as if the user
 * had typed it, two stale values can chase each other around the loop
 * indefinitely: the note visibly flickers between them and only a reload stops
 * it.
 *
 * So local text wins until the round trip catches up. Once the stored copy
 * agrees, or nothing has been typed for {@link SETTLE_MS}, the field goes back
 * to following the database — which is what makes a sync pull or an undo still
 * show up here.
 *
 * @param stored  the value as the database currently has it
 * @param subject the row it belongs to; changing it drops half-typed text,
 *                because that text belonged to the row that just left
 * @returns the text to render, and the callback to hand every local edit to
 */
export function useTypedText(
  stored: string,
  subject?: string,
): [string, (next: string) => void] {
  const [local, setLocal] = useState<string | null>(null);
  const [owner, setOwner] = useState(subject);

  if (subject !== owner) {
    setOwner(subject);
    setLocal(null);
  }

  useEffect(() => {
    if (local === null) return;
    // The round trip caught up: both sides agree, so there is nothing left to
    // protect the field from.
    if (stored === local) {
      setLocal(null);
      return;
    }
    const timer = window.setTimeout(() => setLocal(null), SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [stored, local]);

  // Every field the user types into goes through here, which makes it the one
  // place that knows a note is being written — and the right place to tell sync
  // to wait its turn.
  const type = useCallback((next: string) => {
    noteTyping();
    setLocal(next);
  }, []);

  return [subject !== owner ? stored : (local ?? stored), type];
}
